import { loadStripe, StripeEmbeddedCheckout } from '@stripe/stripe-js';
import { CheckCircle2, Sparkles } from 'lucide-react';
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { PortalOutletContext } from '@/components/portal/PortalShellContainer';
import { loyaltyApi } from '@/lib';
import { isLoyaltyMockClientSecret } from '@/lib/loyaltyMock';
import { formatCurrency, minorToMajor } from '@/lib/currency';
import {
  ApiError,
  LoyaltyEmbeddedCheckoutSession,
  LoyaltyErrorCode,
  LoyaltyPerk,
  LoyaltyPerkTarget,
  LoyaltyTier,
} from '@/types';

const TOS_VERSION = 'v1';
const PASSWORD_MIN = 10;
const PASSWORD_MAX_BYTES = 72;
const EMAIL_RE = /.+@.+\..+/;
const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

type IdentityField = 'firstName' | 'lastName' | 'email' | 'phone';

type Stage = 'details' | 'payment' | 'success';
type Catalog = Record<string, LoyaltyPerkTarget[]>;
type Translate = (s: string, vars?: Record<string, unknown>) => string;

function byteLength(str: string): number {
  return new TextEncoder().encode(str).length;
}

/** "beach_equipment" -> "Beach Equipment" for human-readable perk labels. */
function humanizeBenefit(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** A benefit hits the whole booking when its catalog entry includes booking_total. */
function isBookingBenefit(benefitKey: string, catalog: Catalog): boolean {
  return (catalog[benefitKey] ?? []).some((target) => target.kind === 'booking_total');
}

/** One human-readable line for any perk, generic over the taxonomy (no hardcoded perks). */
function perkLabel(perk: LoyaltyPerk, catalog: Catalog, currency: string, t: Translate): string {
  // Host-authored config copy wins verbatim (untranslated, like tier.label) —
  // the escape hatch for perks the generated copy can't describe.
  if (perk.label) return perk.label;
  const money = (cents: number) => formatCurrency(minorToMajor(cents, currency), currency);
  if (perk.type === 'free_night') {
    return t('Earn a free night every {{n}} nights', { n: perk.thresholdNights });
  }
  if (perk.type === 'stored_credit') {
    // Empty appliesTo = not spendable at checkout (e.g. reimbursement-only), so
    // never claim it's an add-on credit.
    return perk.appliesTo.length > 0
      ? t('{{amount}} annual add-on credit', { amount: money(perk.amountCents) })
      : t('{{amount}} annual credit', { amount: money(perk.amountCents) });
  }
  // discount
  const { mode, amount } = perk.value;
  if (isBookingBenefit(perk.benefitKey, catalog)) {
    if (mode === 'free') return t('Free stays');
    if (mode === 'percent') return t('Save {{pct}}% on every stay', { pct: amount });
    return t('{{amount}} off every stay', { amount: money(amount ?? 0) });
  }
  const subject = humanizeBenefit(perk.benefitKey);
  if (mode === 'free') return t('{{subject}}: free', { subject });
  if (mode === 'percent') return t('{{pct}}% off {{subject}}', { pct: amount, subject });
  return t('{{amount}} off {{subject}}', { amount: money(amount ?? 0), subject });
}

/** The booking-total discount percent for a tier, for the "Save X%" headline. */
function tierBookingDiscountPct(tier: LoyaltyTier, catalog: Catalog): number | null {
  for (const perk of tier.perks) {
    if (perk.type === 'discount' && isBookingBenefit(perk.benefitKey, catalog) && perk.value.mode === 'percent') {
      return perk.value.amount ?? null;
    }
  }
  return null;
}

function formatIntervalLabel(
  interval: 'month' | 'year',
  intervalCount: number | undefined,
  t: (s: string, vars?: Record<string, unknown>) => string
): string {
  const n = intervalCount && intervalCount > 1 ? intervalCount : 1;
  if (interval === 'month') {
    return n === 1 ? t('per month') : t('every {{count}} months', { count: n });
  }
  return n === 1 ? t('per year') : t('every {{count}} years', { count: n });
}

export default function LoyaltySignup() {
  const { t } = useTranslation('portal');
  const navigate = useNavigate();
  const { loyalty, loyaltyLoading, refetchLoyalty } = useOutletContext<PortalOutletContext>();

  const program = loyalty.program;
  const enrolledMembership = loyalty.enrolledMembership;
  const prefill = loyalty.prefill;

  const tiers = program?.config.tiers ?? [];
  const catalog = program?.config.targetCatalog ?? {};

  // A returning/just-enrolled member's actual tier, if any. The success view
  // must render THIS tier's perks, not the first tier's.
  const enrolledTierKey = enrolledMembership?.currentTierKey ?? null;

  // Which tier is selected. For an enrolled member, default to their tier;
  // otherwise the first tier. An effect reconciles once the program/membership
  // load (they can arrive after first render).
  const [selectedTierKey, setSelectedTierKey] = useState<string>(() => enrolledTierKey ?? tiers[0]?.key ?? '');
  useEffect(() => {
    // Enrolled members are pinned to their own tier (it exists in the config —
    // tier keys are stable, plan §2.13). New signups fall back to the first tier.
    if (enrolledTierKey && tiers.some((t) => t.key === enrolledTierKey)) {
      if (selectedTierKey !== enrolledTierKey) setSelectedTierKey(enrolledTierKey);
      return;
    }
    if (!selectedTierKey && tiers.length > 0) setSelectedTierKey(tiers[0].key);
  }, [tiers, selectedTierKey, enrolledTierKey]);
  const selectedTier = tiers.find((tier) => tier.key === selectedTierKey) ?? tiers[0] ?? null;

  const [stage, setStage] = useState<Stage>(enrolledMembership ? 'success' : 'details');

  // Prefill from the reservation we already have on file. Initializer
  // runs once; if prefill arrives later (e.g. tab-focus refetch), the
  // syncing effect below picks it up while the user is still on the
  // details stage.
  const [firstName, setFirstName] = useState(() => prefill?.firstName ?? '');
  const [lastName, setLastName] = useState(() => prefill?.lastName ?? '');
  const [email, setEmail] = useState(() => prefill?.email ?? '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phone, setPhone] = useState(() => prefill?.phone ?? '');
  const [tosAccepted, setTosAccepted] = useState(false);

  // Tracks identity fields the user has interacted with. Once a field is
  // touched (typed into, including cleared), the sync effect must NOT
  // overwrite it on a later prefill refetch (e.g. tab-focus). Without this,
  // clearing a prefilled value and a subsequent refetch would restore the
  // reservation value and clobber the edit.
  const touchedRef = useRef<Set<IdentityField>>(new Set());
  const markTouched = useCallback((field: IdentityField) => {
    touchedRef.current.add(field);
  }, []);
  const onFirstNameChange = useCallback((v: string) => { markTouched('firstName'); setFirstName(v); }, [markTouched]);
  const onLastNameChange = useCallback((v: string) => { markTouched('lastName'); setLastName(v); }, [markTouched]);
  const onEmailChange = useCallback((v: string) => { markTouched('email'); setEmail(v); }, [markTouched]);
  const onPhoneChange = useCallback((v: string) => { markTouched('phone'); setPhone(v); }, [markTouched]);

  // If prefill arrives or changes while the user is still on the details
  // stage, mirror it into untouched fields. Touched fields are left alone
  // so refetch can't clobber a guest edit (including a deliberate clear).
  useEffect(() => {
    if (stage !== 'details' || !prefill) return;
    if (!touchedRef.current.has('firstName') && prefill.firstName) setFirstName(prefill.firstName);
    if (!touchedRef.current.has('lastName') && prefill.lastName) setLastName(prefill.lastName);
    if (!touchedRef.current.has('email') && prefill.email) setEmail(prefill.email);
    if (!touchedRef.current.has('phone') && prefill.phone) setPhone(prefill.phone);
  }, [prefill, stage]);

  // True when we have everything required to skip the identity inputs.
  // Requires a well-formed prefill email so a malformed-but-nonempty value
  // (common in imported PMS data) doesn't hide inputs and trap the guest.
  // Also requires the guest hasn't typed into any identity field; otherwise
  // a late prefill refetch could hide the inputs out from under them.
  const identityKnown =
    !!prefill &&
    !!prefill.firstName &&
    !!prefill.lastName &&
    !!prefill.email &&
    EMAIL_RE.test(prefill.email) &&
    !touchedRef.current.has('firstName') &&
    !touchedRef.current.has('lastName') &&
    !touchedRef.current.has('email');

  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [session, setSession] = useState<LoyaltyEmbeddedCheckoutSession | null>(null);

  useEffect(() => {
    document.title = t('Join our loyalty program');
  }, [t]);

  // Returning visitor with an existing membership lands directly on success.
  // We only auto-flip when we're still on the details stage: once the guest
  // is actively in the embedded checkout, a refetch (e.g. tab focus) that
  // surfaces the pending row we just created must NOT unmount the Stripe
  // iframe mid-payment. handlePaymentComplete drives details/payment ->
  // success explicitly when Stripe says so.
  useEffect(() => {
    if (enrolledMembership && stage === 'details') setStage('success');
  }, [enrolledMembership, stage]);

  // Stable callback handed to <EmbeddedPayment>. If we inlined the arrow,
  // every parent re-render (e.g. refetchLoyalty firing on tab focus updates
  // the outlet `loyalty` state -> shell re-renders -> we re-render) would
  // change its identity, retriggering EmbeddedPayment's mount effect and
  // tearing down the live Stripe Embedded Checkout mid-payment.
  const handlePaymentComplete = useCallback(() => {
    setStage('success');
    void refetchLoyalty();
  }, [refetchLoyalty]);

  const passwordTooShort = password.length > 0 && password.length < PASSWORD_MIN;
  const passwordTooLong = password.length > 0 && byteLength(password) > PASSWORD_MAX_BYTES;
  const passwordMismatch = confirmPassword.length > 0 && password !== confirmPassword;

  const canSubmit = useMemo(() => {
    return (
      !submitting &&
      !!program &&
      !!selectedTier &&
      firstName.trim().length > 0 &&
      lastName.trim().length > 0 &&
      EMAIL_RE.test(email.trim()) &&
      password.length >= PASSWORD_MIN &&
      byteLength(password) <= PASSWORD_MAX_BYTES &&
      password === confirmPassword &&
      tosAccepted
    );
  }, [submitting, program, selectedTier, firstName, lastName, email, password, confirmPassword, tosAccepted]);

  function loyaltyErrorMessage(code: LoyaltyErrorCode | null | undefined): string {
    switch (code) {
      case 'email_taken':
        return t('An account with this email already exists. Sign in to continue.');
      case 'already_active_membership':
        return t('You already have an active membership in this program.');
      case 'already_pending_membership':
        return t('Your membership is pending checkout. Check your email or try again shortly.');
      case 'host_stripe_not_ready':
        return t('This program is not ready for sign-up. Please contact support.');
      case 'program_disabled':
      case 'program_not_purchasable':
      case 'program_not_found':
        return t('This program is not available right now.');
      case 'invalid_tier':
        return t('Please choose a membership tier and try again.');
      case 'invalid_input':
        return t('Please check your information and try again.');
      default:
        return t('Something went wrong. Please try again.');
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!program || !selectedTier || !canSubmit) return;
    if (!STRIPE_PUBLISHABLE_KEY) {
      setErrorMessage(t('Payments are not configured. Please contact support.'));
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);

    try {
      const result = await loyaltyApi.signup({
        email: email.trim(),
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim() || null,
        tosAcceptedVersion: TOS_VERSION,
        tierKey: selectedTier.key,
      });
      setSession(result);
      setStage('payment');
      setSubmitting(false);
    } catch (err) {
      const code: LoyaltyErrorCode =
        (err instanceof ApiError ? (err.code as LoyaltyErrorCode | undefined) : undefined) ??
        'server_error';
      setErrorMessage(loyaltyErrorMessage(code));
      setSubmitting(false);
    }
  }

  // Visibility is now driven entirely by whether the host has a loyalty
  // program row, surfaced as loyalty.program from the shell. While the very
  // first fetch is still in flight, show a spinner instead of mis-rendering
  // null as "unavailable" (deep links to /portal/home/loyalty can land here
  // before the fetch resolves).
  if (loyaltyLoading && !program) {
    return (
      <CenteredCard>
        <div className="flex items-center justify-center py-2">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-zinc-200 border-t-[#1E3A5F]" />
        </div>
      </CenteredCard>
    );
  }
  if (!program) {
    return <CenteredCard>{t("This program isn't available right now. Please try again later.")}</CenteredCard>;
  }

  const tier = selectedTier;
  const currency = tier?.currency ?? 'usd';
  // Tiered enrollment checks out against the tier's fixed Stripe Price, so the
  // amount charged is always the listed tier price. We intentionally do NOT
  // apply an intro discount here: a "Save X%" enrollment offer needs a Stripe
  // coupon (not yet built), and showing a discounted price the checkout won't
  // honor would charge the guest more than displayed.
  const chargedPriceMajor = tier ? minorToMajor(tier.priceCents, currency) : 0;
  const chargedPriceLabel = tier ? formatCurrency(chargedPriceMajor, currency) : '';
  const intervalLabel = tier ? formatIntervalLabel(tier.interval, tier.intervalCount, t) : '';
  const tierPerkLines = tier ? tier.perks.map((perk) => perkLabel(perk, catalog, currency, t)) : [];

  if (stage === 'success') {
    // Returning member came in with an existing enrollment (no signup flow ran
    // this session). Discriminating on session === null avoids the slim race
    // where refetchLoyalty resolves enrolledMembership just after the user
    // completes Stripe -- in that case session is set and we still want the
    // "you just joined" tone.
    const isReturning = enrolledMembership !== null && session === null;
    return (
      <PageContainer>
        <SuccessState
          programName={program.name || t('Loyalty program')}
          perkLines={tierPerkLines}
          isReturning={isReturning}
          onBackToPortal={() => navigate('/portal/home')}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="bg-white rounded-2xl border border-zinc-200/70 shadow-sm overflow-hidden">
        <div className="px-6 sm:px-8 pt-8 pb-6">
          <div className="w-11 h-11 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center">
            <Sparkles className="w-5 h-5" />
          </div>
          <h1 className="mt-4 text-[26px] font-semibold text-zinc-900 tracking-tight">
            {program.name || t('Loyalty program')}
          </h1>
          {program.description && (
            <p className="mt-2 text-[14px] text-zinc-600 leading-relaxed">{program.description}</p>
          )}

          {tiers.length > 1 && (
            <TierSelector
              t={t}
              tiers={tiers}
              catalog={catalog}
              selectedKey={selectedTier?.key ?? ''}
              onSelect={setSelectedTierKey}
            />
          )}

          {tier && (
            <div className="mt-5">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-[28px] font-semibold text-zinc-900 tabular-nums">
                  {chargedPriceLabel}
                </span>
                <span className="text-[13px] text-zinc-500">{intervalLabel}</span>
              </div>
            </div>
          )}

          {tierPerkLines.length > 0 && (
            <ul className="mt-5 space-y-2.5 text-[14px] text-zinc-700">
              {tierPerkLines.map((label) => (
                <PerkLine key={label} label={label} />
              ))}
            </ul>
          )}
        </div>

        {stage === 'details' && (
          <DetailsForm
            t={t}
            firstName={firstName}
            lastName={lastName}
            email={email}
            password={password}
            confirmPassword={confirmPassword}
            phone={phone}
            tosAccepted={tosAccepted}
            passwordTooShort={passwordTooShort}
            passwordTooLong={passwordTooLong}
            passwordMismatch={passwordMismatch}
            canSubmit={canSubmit}
            submitting={submitting}
            errorMessage={errorMessage}
            chargedPriceLabel={chargedPriceLabel}
            identityKnown={identityKnown}
            knownPhone={prefill?.phone ?? null}
            onFirstName={onFirstNameChange}
            onLastName={onLastNameChange}
            onEmail={onEmailChange}
            onPassword={setPassword}
            onConfirmPassword={setConfirmPassword}
            onPhone={onPhoneChange}
            onTos={setTosAccepted}
            onSubmit={handleSubmit}
          />
        )}

        {stage === 'payment' && session && (
          <EmbeddedPayment
            session={session}
            onComplete={handlePaymentComplete}
            chargedPriceLabel={chargedPriceLabel}
          />
        )}
      </div>
    </PageContainer>
  );
}

// ─── Pieces ────────────────────────────────────────────────────────────────

function PageContainer({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-4 sm:px-6 lg:px-10 pt-8 lg:pt-12 pb-16">
      <div className="max-w-2xl mx-auto">{children}</div>
    </div>
  );
}

function CenteredCard({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation('portal');
  return (
    <PageContainer>
      <div className="bg-white rounded-2xl border border-zinc-200/70 shadow-sm p-8 text-center">
        <h2 className="text-[22px] font-semibold text-zinc-900 tracking-tight">
          {t('Not available')}
        </h2>
        <p className="mt-2 text-[14px] text-zinc-600">{children}</p>
      </div>
    </PageContainer>
  );
}

function KnownIdentitySummary(props: {
  t: (s: string, vars?: Record<string, unknown>) => string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
}) {
  const { t, firstName, lastName, email, phone } = props;
  const fullName = `${firstName} ${lastName}`.trim();
  return (
    <div className="rounded-lg bg-zinc-50 border border-zinc-100 px-4 py-3">
      <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-zinc-500">
        {t('Creating account for')}
      </div>
      <div className="mt-1 text-[14px] font-medium text-zinc-900">{fullName}</div>
      <div className="text-[13px] text-zinc-600">{email}</div>
      {phone && <div className="text-[13px] text-zinc-600">{phone}</div>}
    </div>
  );
}

function PerkLine({ label }: { label: string }) {
  return (
    <li className="flex items-start gap-2.5">
      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#1E3A5F] flex-shrink-0" />
      <span>{label}</span>
    </li>
  );
}

function DetailsForm(props: {
  t: (s: string, vars?: Record<string, unknown>) => string;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  phone: string;
  tosAccepted: boolean;
  passwordTooShort: boolean;
  passwordTooLong: boolean;
  passwordMismatch: boolean;
  canSubmit: boolean;
  submitting: boolean;
  errorMessage: string | null;
  chargedPriceLabel: string;
  identityKnown: boolean;
  knownPhone: string | null;
  onFirstName: (v: string) => void;
  onLastName: (v: string) => void;
  onEmail: (v: string) => void;
  onPassword: (v: string) => void;
  onConfirmPassword: (v: string) => void;
  onPhone: (v: string) => void;
  onTos: (v: boolean) => void;
  onSubmit: (e: FormEvent) => void;
}) {
  const {
    t,
    firstName,
    lastName,
    email,
    password,
    confirmPassword,
    phone,
    tosAccepted,
    passwordTooShort,
    passwordTooLong,
    passwordMismatch,
    canSubmit,
    submitting,
    errorMessage,
    chargedPriceLabel,
    identityKnown,
    knownPhone,
    onFirstName,
    onLastName,
    onEmail,
    onPassword,
    onConfirmPassword,
    onPhone,
    onTos,
    onSubmit,
  } = props;

  return (
    <form onSubmit={onSubmit} className="px-6 sm:px-8 pb-8 pt-2 space-y-4">
      {identityKnown ? (
        <KnownIdentitySummary
          t={t}
          firstName={firstName}
          lastName={lastName}
          email={email}
          phone={knownPhone}
        />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('First name')} required>
              <input
                type="text"
                autoComplete="given-name"
                required
                value={firstName}
                onChange={(e) => onFirstName(e.target.value)}
                className="w-full h-11 rounded-lg border border-zinc-200 px-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/30"
              />
            </Field>
            <Field label={t('Last name')} required>
              <input
                type="text"
                autoComplete="family-name"
                required
                value={lastName}
                onChange={(e) => onLastName(e.target.value)}
                className="w-full h-11 rounded-lg border border-zinc-200 px-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/30"
              />
            </Field>
          </div>

          <Field label={t('Email')} required>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => onEmail(e.target.value)}
              className="w-full h-11 rounded-lg border border-zinc-200 px-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/30"
            />
          </Field>
        </>
      )}

      <Field
        label={t('Password')}
        required
        hint={t('At least {{n}} characters', { n: PASSWORD_MIN })}
        error={
          passwordTooShort
            ? t('At least {{n}} characters', { n: PASSWORD_MIN })
            : passwordTooLong
              ? t('Password is too long')
              : null
        }
      >
        <input
          type="password"
          autoComplete="new-password"
          required
          value={password}
          onChange={(e) => onPassword(e.target.value)}
          className="w-full h-11 rounded-lg border border-zinc-200 px-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/30"
        />
      </Field>

      <Field
        label={t('Confirm password')}
        required
        error={passwordMismatch ? t('Passwords do not match') : null}
      >
        <input
          type="password"
          autoComplete="new-password"
          required
          value={confirmPassword}
          onChange={(e) => onConfirmPassword(e.target.value)}
          className="w-full h-11 rounded-lg border border-zinc-200 px-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/30"
        />
      </Field>

      {!identityKnown && (
        <Field label={t('Phone (optional)')}>
          <input
            type="tel"
            autoComplete="tel"
            value={phone}
            onChange={(e) => onPhone(e.target.value)}
            className="w-full h-11 rounded-lg border border-zinc-200 px-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/30"
          />
        </Field>
      )}

      <label className="flex items-start gap-2.5 text-[13px] text-zinc-600 pt-1">
        <input
          type="checkbox"
          checked={tosAccepted}
          onChange={(e) => onTos(e.target.checked)}
          className="mt-0.5"
        />
        <span>
          {t('I agree to the loyalty program terms and recurring billing via Stripe.')}
        </span>
      </label>

      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-[13px] text-red-700">
          {errorMessage}
        </div>
      )}

      <button
        type="submit"
        disabled={!canSubmit}
        className="w-full h-12 rounded-full text-white text-[15px] font-medium transition disabled:opacity-50"
        style={{
          backgroundColor: canSubmit ? 'var(--besty-blue)' : 'var(--besty-blue-disabled)',
        }}
      >
        {submitting
          ? t('Setting up your account...')
          : t('Continue to payment ({{price}})', { price: chargedPriceLabel })}
      </button>

      <p className="text-[11px] text-zinc-400 leading-relaxed pt-1">
        {t('Card details are collected and processed by Stripe on the next step.')}
      </p>
    </form>
  );
}

function EmbeddedPayment(props: {
  session: LoyaltyEmbeddedCheckoutSession;
  onComplete: () => void;
  chargedPriceLabel: string;
}) {
  const { session, onComplete, chargedPriceLabel } = props;
  const { t } = useTranslation('portal');
  const mountRef = useRef<HTMLDivElement | null>(null);
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const isMock = isLoyaltyMockClientSecret(session.clientSecret);

  useEffect(() => {
    if (isMock) return;
    if (!STRIPE_PUBLISHABLE_KEY) {
      setStripeError(t('Payments are not configured.'));
      return;
    }
    let cancelled = false;
    let instance: StripeEmbeddedCheckout | null = null;
    (async () => {
      try {
        const stripe = await loadStripe(STRIPE_PUBLISHABLE_KEY, {
          stripeAccount: session.connectedAccountId,
        });
        if (cancelled) return;
        if (!stripe) {
          setStripeError(t("Couldn't load the payment form. Please refresh and try again."));
          return;
        }
        instance = await stripe.initEmbeddedCheckout({
          clientSecret: session.clientSecret,
          onComplete: () => {
            // Stripe fires onComplete after a successful session completion.
            // We optimistically flip to the success state; the membership row
            // flips pending -> active when the (separately-owned) webhook
            // subscriber lands.
            setProcessing(true);
            onComplete();
          },
        });
        if (cancelled) {
          instance.destroy();
          return;
        }
        if (mountRef.current) instance.mount(mountRef.current);
      } catch (err) {
        if (cancelled) return;
        console.error('LoyaltySignup: failed to init embedded checkout', err);
        setStripeError(t("Couldn't load the payment form. Please refresh and try again."));
      }
    })();
    return () => {
      cancelled = true;
      if (instance) {
        try {
          instance.destroy();
        } catch {
          // ignore — best-effort cleanup
        }
      }
    };
  }, [isMock, session, onComplete, t]);

  return (
    <div className="px-6 sm:px-8 pb-8 pt-2">
      <div className="rounded-lg bg-zinc-50 border border-zinc-100 px-4 py-3 mb-4 flex items-center justify-between">
        <span className="text-[13px] text-zinc-600">{t('Amount due today')}</span>
        <span className="text-[15px] font-semibold text-zinc-900 tabular-nums">
          {chargedPriceLabel}
        </span>
      </div>

      {isMock ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-5 min-h-[450px] flex flex-col items-center justify-center gap-3">
          <div className="text-[12px] uppercase tracking-wider text-amber-700 font-semibold">
            Mock mode (no real charge)
          </div>
          <div className="text-[13px] text-amber-900 text-center max-w-sm">
            Stripe Embedded Checkout is bypassed because ?mockLoyalty is active.
            Click below to simulate the result your guest would see.
          </div>
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              className="rounded-md bg-emerald-600 px-4 py-2 text-[13px] font-medium text-white hover:bg-emerald-700"
              onClick={() => {
                setProcessing(true);
                onComplete();
              }}
            >
              Simulate payment success
            </button>
            <button
              type="button"
              className="rounded-md bg-zinc-200 px-4 py-2 text-[13px] font-medium text-zinc-900 hover:bg-zinc-300"
              onClick={() => setStripeError('Simulated payment cancellation.')}
            >
              Simulate cancel
            </button>
          </div>
        </div>
      ) : stripeError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-[13px] text-red-700">
          {stripeError}
        </div>
      ) : (
        <div ref={mountRef} className="min-h-[450px]" />
      )}

      {processing && (
        <div className="mt-4 flex items-center justify-center gap-2 text-[13px] text-zinc-500">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-zinc-200 border-t-[#1E3A5F]" />
          {t('Processing payment...')}
        </div>
      )}
    </div>
  );
}

function TierSelector(props: {
  t: Translate;
  tiers: LoyaltyTier[];
  catalog: Catalog;
  selectedKey: string;
  onSelect: (key: string) => void;
}) {
  const { t, tiers, catalog, selectedKey, onSelect } = props;
  return (
    <div className="mt-5 grid gap-2.5" role="radiogroup" aria-label={t('Choose a tier')}>
      {tiers.map((tier) => {
        const selected = tier.key === selectedKey;
        const pct = tierBookingDiscountPct(tier, catalog);
        const priceLabel = formatCurrency(minorToMajor(tier.priceCents, tier.currency), tier.currency);
        return (
          <button
            type="button"
            key={tier.key}
            role="radio"
            aria-checked={selected}
            onClick={() => onSelect(tier.key)}
            className={`flex items-center justify-between rounded-xl border px-4 py-3 text-left transition ${
              selected
                ? 'border-[#1E3A5F] ring-2 ring-[#1E3A5F]/20 bg-[#1E3A5F]/[0.03]'
                : 'border-zinc-200 hover:border-zinc-300'
            }`}
          >
            <div>
              <div className="text-[15px] font-semibold text-zinc-900">{tier.label}</div>
              {pct != null && (
                <div className="text-[12px] text-zinc-500">{t('Save {{pct}}% on every stay', { pct })}</div>
              )}
            </div>
            <div className="text-[14px] font-semibold text-zinc-900 tabular-nums">{priceLabel}</div>
          </button>
        );
      })}
    </div>
  );
}

function SuccessState(props: {
  programName: string;
  perkLines: string[];
  isReturning: boolean;
  onBackToPortal: () => void;
}) {
  const { programName, perkLines, isReturning, onBackToPortal } = props;
  const { t } = useTranslation('portal');
  return (
    <div className="bg-white rounded-2xl border border-zinc-200/70 shadow-sm overflow-hidden">
      <div className="px-6 sm:px-8 pt-10 pb-8 text-center">
        <div className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-7 h-7" />
        </div>
        <h1 className="mt-5 text-[26px] font-semibold text-zinc-900 tracking-tight">
          {isReturning
            ? t('Thanks for being a member of {{name}}', { name: programName })
            : t("You're a member of {{name}}", { name: programName })}
        </h1>
        <p className="mt-2 text-[14px] text-zinc-600 leading-relaxed">
          {isReturning ? t('Your perks are active.') : t('Your perks are now active. Enjoy your stay!')}
        </p>

        {perkLines.length > 0 && (
          <ul className="mt-6 space-y-2.5 text-[14px] text-zinc-700 text-left max-w-sm mx-auto">
            {perkLines.map((label) => (
              <PerkLine key={label} label={label} />
            ))}
          </ul>
        )}

        <button
          type="button"
          onClick={onBackToPortal}
          className="mt-8 w-full sm:w-auto px-6 h-12 rounded-full text-white text-[15px] font-medium transition"
          style={{ backgroundColor: 'var(--besty-blue)' }}
        >
          {t('Back to home')}
        </button>
      </div>
    </div>
  );
}

function Field({
  label,
  required,
  hint,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  error?: string | null;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-[12px] font-medium text-zinc-700 mb-1.5">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {error ? (
        <div className="mt-1 text-[12px] text-red-600">{error}</div>
      ) : hint ? (
        <div className="mt-1 text-[12px] text-zinc-400">{hint}</div>
      ) : null}
    </div>
  );
}
